// Subscription tier limits configuration — FREE / PRO / POWER

import type { PlanTier, TierConfig } from '../types/plan';

export type SubscriptionTier = PlanTier;

export interface TierLimits extends TierConfig {
  aiSearches: number;       // per-month AI search cap (-1 unlimited)
  aiAnalyses: number;       // per-month AI image analyses cap
  exports: number;           // per-month export cap
  cloudBackup: boolean;
}

// Drive items limit can be overridden via env (FREE tier only)
const FREE_DRIVE_LIMIT = Math.min(
  2000,
  Math.max(1000, Number(
    typeof process !== 'undefined' && process.env?.FREE_DRIVE_LIMIT
      ? process.env.FREE_DRIVE_LIMIT
      : (typeof import.meta !== 'undefined' ? (import.meta as any).env?.VITE_FREE_DRIVE_LIMIT : undefined)
  ) || 2000)
);

export const TIER_LIMITS: Record<SubscriptionTier, TierLimits> = {
  free: {
    driveItemsLimit: FREE_DRIVE_LIMIT,
    localItemsLimit: -1,               // unlimited
    aiSearchTags: false,                // no Gemini tag generation
    collectionsEnabled: false,
    collectionsLimit: 0,
    premiumFeatures: [],
    aiSearches: 10,                     // per month (keyword-only fallback)
    aiAnalyses: 0,
    exports: 0,
    cloudBackup: false,
  },
  pro: {
    driveItemsLimit: 50_000,
    localItemsLimit: -1,
    aiSearchTags: true,
    collectionsEnabled: true,
    collectionsLimit: 50,
    premiumFeatures: [],
    aiSearches: -1,                     // unlimited
    aiAnalyses: 100,
    exports: -1,
    cloudBackup: true,
  },
  power: {
    driveItemsLimit: 200_000,
    localItemsLimit: -1,
    aiSearchTags: true,
    collectionsEnabled: true,
    collectionsLimit: 500,
    premiumFeatures: ['smartShuffle', 'memoryRecaps', 'priorityProcessing'],
    aiSearches: -1,
    aiAnalyses: -1,
    exports: -1,
    cloudBackup: true,
  },
};

export const TIER_PRICING: Record<SubscriptionTier, { monthly: number; yearly: number }> = {
  free: { monthly: 0, yearly: 0 },
  pro: { monthly: 5.99, yearly: 57.99 },
  power: { monthly: 19.99, yearly: 199.99 },
};

export const TIER_DISPLAY_NAME: Record<SubscriptionTier, string> = {
  free: 'Free',
  pro: 'Pro',
  power: 'Power',
};

export function getTierLimits(tier: SubscriptionTier): TierLimits {
  return TIER_LIMITS[tier] || TIER_LIMITS.free;
}
